'use client';

import AutoRepliesModal from '@/components/AutoRepliesModal';
import { createClientLogger, logClientWarning } from '@/client/logging/client-logger';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type AutoRepliesModalOpenOptions = {
    onClose?: () => void;
};

type AutoRepliesModalContextValue = {
    openAutoRepliesModal: (options?: AutoRepliesModalOpenOptions) => void;
};

const autoRepliesModalLogger = createClientLogger({
    scope: 'auto-replies-modal',
});

const missingProviderContext: AutoRepliesModalContextValue = {
    openAutoRepliesModal: () => {
        autoRepliesModalLogger.warn(
            '[client.auto-replies-modal.open] Skipped auto-replies modal open without provider.',
            {
                outcome: 'missing_provider',
            },
        );
    },
};

const AutoRepliesModalContext = createContext<AutoRepliesModalContextValue>(missingProviderContext);

export const useAutoRepliesModal = (): AutoRepliesModalContextValue => useContext(AutoRepliesModalContext);

export const AutoRepliesModalProvider = ({ children }: PropsWithChildren) => {
    const [isAutoRepliesModalOpen, setIsAutoRepliesModalOpen] = useState(false);
    const isAutoRepliesModalOpenRef = useRef(false);
    const closeContinuationRef = useRef<(() => void) | null>(null);

    const runCloseContinuation = useCallback(() => {
        const closeContinuation = closeContinuationRef.current;
        closeContinuationRef.current = null;

        if (!closeContinuation) {
            return;
        }

        try {
            closeContinuation();
        } catch (error) {
            logClientWarning({
                logger: autoRepliesModalLogger,
                message:
                    '[client.auto-replies-modal.close-continuation] Failed to run auto-replies modal close continuation.',
                error,
                context: {
                    operation: 'auto_replies_modal_close_continuation',
                },
            });
        }
    }, []);

    const setAutoRepliesModalShown = useCallback(
        (nextIsShown: boolean) => {
            const wasOpen = isAutoRepliesModalOpenRef.current;
            isAutoRepliesModalOpenRef.current = nextIsShown;
            setIsAutoRepliesModalOpen(nextIsShown);

            if (!nextIsShown && wasOpen) {
                runCloseContinuation();
            }
        },
        [runCloseContinuation],
    );

    const openAutoRepliesModal = useCallback((options?: AutoRepliesModalOpenOptions) => {
        closeContinuationRef.current = options?.onClose ?? null;
        isAutoRepliesModalOpenRef.current = true;
        autoRepliesModalLogger.info('[client.auto-replies-modal.open] Accepted auto-replies modal open request.', {
            outcome: 'accepted',
            hasCloseContinuation: typeof options?.onClose === 'function',
        });
        setIsAutoRepliesModalOpen(true);
    }, []);

    const value = useMemo(
        () => ({
            openAutoRepliesModal,
        }),
        [openAutoRepliesModal],
    );

    return (
        <AutoRepliesModalContext.Provider value={value}>
            {children}
            <AutoRepliesModal isShown={isAutoRepliesModalOpen} setIsShown={setAutoRepliesModalShown} />
        </AutoRepliesModalContext.Provider>
    );
};
